// Component types of the form builder (the keys match the backend builder model).
export const COMPONENT_TYPES = {
  heading: { label: "Large heading", group: "Text" },
  subheading: { label: "Small heading", group: "Text" },
  body: { label: "Body", group: "Text" },
  caption: { label: "Caption", group: "Text" },
  image: { label: "Image", group: "Media" },
  link: { label: "Link", group: "Media" },
  short_answer: { label: "Short answer", group: "Text answer" },
  paragraph: { label: "Paragraph", group: "Text answer" },
  date: { label: "Date picker", group: "Text answer" },
  single_choice: { label: "Single choice", group: "Selection" },
  multiple_choice: { label: "Multiple choice", group: "Selection" },
  dropdown: { label: "Dropdown", group: "Selection" },
  opt_in: { label: "Opt-in", group: "Selection" },
};

export const TYPE_GROUPS = ["Text", "Media", "Text answer", "Selection"];

export const INPUT_TYPE_OPTIONS = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
  { label: "Password", value: "password" },
  { label: "Passcode", value: "passcode" },
];

export const isTextType = (type) =>
  ["heading", "subheading", "body", "caption"].includes(type);
export const isChoiceType = (type) =>
  ["single_choice", "multiple_choice", "dropdown"].includes(type);
export const isInputType = (type) =>
  !isTextType(type) && !["image", "link"].includes(type) && Boolean(COMPONENT_TYPES[type]);

const hex = (bytes) => {
  const values = new Uint8Array(bytes);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(values);
  } else {
    values.forEach((_, i) => {
      values[i] = Math.floor(Math.random() * 256);
    });
  }
  return Array.from(values, (b) => b.toString(16).padStart(2, "0")).join("");
};

export const newComponentId = () => hex(4);
export const newOptionId = () => `opt_${hex(3)}`;

export const newComponent = (type) => {
  const base = { id: newComponentId(), type };
  const label = COMPONENT_TYPES[type].label;
  if (isTextType(type)) return { ...base, text: label };
  if (type === "image") return { ...base, alt: "Image", image_id: null };
  if (type === "link") return { ...base, text: "Visit our website", url: "" };
  const input = { ...base, label, key: "", required: false };
  if (isChoiceType(type)) {
    input.options = [
      { id: newOptionId(), title: "Option 1" },
      { id: newOptionId(), title: "Option 2" },
    ];
  }
  if (type === "opt_in") input.label = "I agree to receive updates";
  return input;
};

// A copy of a component with its own ids. The field key is cleared so the server
// generates a unique one from the label (a copied key would be a duplicate).
export const copyComponent = (component) => {
  const copy = { ...JSON.parse(JSON.stringify(component)), id: newComponentId() };
  if (isInputType(copy.type)) copy.key = "";
  if (copy.options) copy.options = copy.options.map((o) => ({ ...o, id: newOptionId() }));
  return copy;
};

// The server fills in the screen id from the title on the first save.
export const newScreen = () => ({
  title: "New screen",
  components: [newComponent("subheading")],
  footer: { label: "Continue" },
});

export const componentSummary = (component) => {
  if (isTextType(component.type) || component.type === "link") return component.text || "";
  if (component.type === "image") return component.alt || "";
  return component.label || "";
};

export const toFieldKey = (value) => String(value || "").replace(/[^A-Za-z0-9_]/g, "_");

// { field key: [{ id, label, screenIndex }] }, so a duplicate key is flagged while
// typing (the backend's duplicate_key check still blocks test sends and publishing).
export const fieldKeyOwners = (model) => {
  const owners = {};
  (model?.screens || []).forEach((screen, screenIndex) =>
    (screen.components || []).forEach((c) => {
      if (isInputType(c.type) && c.key) {
        (owners[c.key] = owners[c.key] || []).push({ id: c.id, label: c.label, screenIndex });
      }
    }),
  );
  return owners;
};

// "Show only when": component.visible_when = [{ component_id, op, value }], all of
// which must hold (the backend checks and publishes them as nested If components).
export const RULE_FIELD_TYPES = ["short_answer", "paragraph", "single_choice", "dropdown"];
export const RULE_OPS = {
  "==": "is",
  "!=": "is not",
  ">": "is more than",
  "<": "is less than",
  ">=": "is at least",
  "<=": "is at most",
};
export const NUMERIC_OPS = [">", "<", ">=", "<="];
export const MAX_RULES = 3; // WhatsApp allows If nested 3 deep

export const isNumberField = (component) =>
  component?.type === "short_answer" && component.input_type === "number";

// Questions a rule on screen `screenIndex` can use: that screen's and earlier ones'.
export const ruleFieldsFor = (model, screenIndex) =>
  (model?.screens || []).slice(0, screenIndex + 1).flatMap((screen, i) =>
    (screen.components || [])
      .filter((c) => RULE_FIELD_TYPES.includes(c.type))
      .map((component) => ({
        component,
        sameScreen: i === screenIndex,
        where: i === screenIndex ? "this screen" : screen.title || `Screen ${i + 1}`,
      })),
  );

// Conditional next screen: screen.footer.branch = { component_id, targets: { option id:
// screen id } }. One screen may branch; every later screen is a target and ends the form.
export const BRANCH_FIELD_TYPES = ["single_choice", "dropdown"];
export const findBranchIndex = (model) =>
  (model?.screens || []).findIndex((screen) => screen.footer?.branch?.component_id);

// Whether all of a component's rules hold for answers ({ component id: value }).
export const rulesHold = (rules, answers) =>
  (rules || []).every((rule) => {
    const answer = answers[rule.component_id];
    if (NUMERIC_OPS.includes(rule.op)) {
      const a = parseFloat(answer);
      const b = parseFloat(rule.value);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      return { ">": a > b, "<": a < b, ">=": a >= b, "<=": a <= b }[rule.op];
    }
    const same = String(answer ?? "") === String(rule.value ?? "");
    return rule.op === "!=" ? !same : same;
  });

// Copies what the server generated (field keys, and screen ids when asked) onto a
// local model, without touching anything else the user may have typed since.
// Screen ids are matched by position, so only pass fillScreenIds for the live model.
export const mergeServerModel = (local, server, { fillScreenIds = true } = {}) => {
  if (!server?.screens) return local;
  const keys = {};
  server.screens.forEach((serverScreen) =>
    (serverScreen.components || []).forEach((c) => {
      if (c.key) keys[c.id] = c.key;
    }),
  );
  return {
    ...local,
    screens: local.screens.map((screen, si) => ({
      ...screen,
      id: screen.id || (fillScreenIds ? server.screens[si]?.id : undefined),
      components: screen.components.map((c) =>
        isInputType(c.type) && !c.key && keys[c.id] ? { ...c, key: keys[c.id] } : c,
      ),
    })),
  };
};
